import { type SupabaseClient, createClient } from '@supabase/supabase-js';
import { getZooConfig } from '../auth/onboarding';

/**
 * Supabase client singleton.
 *
 * Both staging and production use the same flow:
 *   1. Stored ZooConfig from onboarding — Platform API (staging or production)
 *      resolves the correct Supabase instance per zoo.
 *   2. Build-time env vars — fallback for dev/CI/fresh install before onboarding.
 *
 * The client is lazily created and cached. Call `resetSupabase()` when
 * the zoo config changes (e.g. after onboarding or "Switch Zoo").
 */

let _client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient | null {
  if (_client) return _client;

  // Platform-resolved ZooConfig takes priority (both staging and production).
  const zooConfig = getZooConfig();
  if (zooConfig) {
    _client = createClient(zooConfig.supabaseUrl, zooConfig.supabaseAnonKey);
    return _client;
  }

  // Fallback: build-time env vars (dev / CI / fresh install before onboarding).
  const supabaseUrl = process.env.SUPABASE_URL;
  const supabaseAnonKey = process.env.SUPABASE_ANON_KEY;
  if (supabaseUrl && supabaseAnonKey) {
    _client = createClient(supabaseUrl, supabaseAnonKey);
    return _client;
  }

  console.warn('Supabase not configured: no zoo config and no env vars. Auth will not work.');
  return null;
}

/**
 * Reset the cached client. Call after onboarding completes or zoo config changes.
 */
export function resetSupabase(): void {
  _client = null;
}
